import React, { FunctionComponent, ReactNode, useState, JSX } from 'react';
import { FormattedList, FormattedMessage, useIntl } from 'react-intl';
import {
  toMoneyValue,
  ComputedPaymentTypeValues,
} from '@ads-bread/shared/bread/util';
import { useFormattedNumber } from '../../../lib/hooks/useFormattedNumber';
import {
  PageNames,
  useTrackPageView,
} from '../../../lib/hooks/useTrackPageView';
import { ButtonClickedEvents } from '../../../lib/analytics';
import { BreadLogo } from '../../../components/svgs/BreadLogo';
import IntroScreen, {
  formattingValues,
  IntroTitle,
  BreadBrandingSubheader,
} from '../../../components/pages/intro/Intro';
import { IntroCard } from '../../../components/IntroCard';
import { RouteMachineServiceScreenProps } from '../../../components/RouteMachineService';
import { Installments } from '../../../components/svgs/Installments';
import { SplitPay } from '../../../components/svgs/SplitPay';
import { SVGIcon } from '../../../components/svgs/svgHelpers';
import { Accordion, Section } from '../../../components/Accordion';
import VerticalProgressBar from '../../../components/VerticalProgressBar';
import SlideOut from '../../../components/SlideOut';
import { useVirtualCard } from '../../../components/XPropsContext';
import { Info } from '../../../components/svgs/Info';
import { Tooltip, TooltipParagraph } from '../../../components/Tooltip';
import { Button } from '../../../components/Button';
import { EligibilityCheckHelp } from '../../../components/EligibilityCheckHelp';
import { useMerchantConfig } from '../../../components/MerchantContext';
import {
  Disclaimers,
  Disclaimer,
} from '../../../components/pages/intro/Disclaimers';

export const Intro: FunctionComponent<RouteMachineServiceScreenProps> = (
  props
) => {
  return (
    <IntroScreen {...props}>
      {({
        hasSplitpay,
        hasInstallments,
        hasHybridLineOfCredit,
        synthesizedSplitPayPaymentValues,
        exampleInstallmentsPaymentTypeValues,
        exampleHybridLineOfCreditPaymentTypeValues,
      }) => {
        return (
          <>
            <IntroTitle>
              <FormattedMessage
                defaultMessage="Pay Over Time"
                description="Intro screen heading"
              />
            </IntroTitle>
            <BreadBrandingSubheader />
            <InstallmentsCard
              installmentIcon={Installments}
              paymentTypeValues={exampleInstallmentsPaymentTypeValues}
            />

            {hasHybridLineOfCredit && (
              <HybridLineOfCreditCard
                installmentIcon={Installments}
                paymentTypeValues={exampleHybridLineOfCreditPaymentTypeValues}
              />
            )}

            <SplitPayCard
              splitPayIcon={SplitPay}
              paymentTypeValues={synthesizedSplitPayPaymentValues}
            />
            <div className="bg-merchant bg-opacity-20 text-center my-8 px-1 font-normal">
              <FormattedMessage
                defaultMessage="Checking your eligibility is quick, easy,
          and won’t affect your credit <NoWrapTooltip>score</NoWrapTooltip>"
                description="Intro screen body"
                values={{
                  NoWrapTooltip: (chunks: ReactNode) => (
                    <span className="whitespace-nowrap">
                      {chunks}

                      {EligibilityCheckHelp && (
                        <EligibilityCheckHelp dataTestID="intro-eligibility-check-help" />
                      )}
                    </span>
                  ),
                }}
              />
            </div>

            <Button
              analyticsEventName={ButtonClickedEvents.GetStarted}
              onClick={props.forward}
            >
              <FormattedMessage
                defaultMessage="Get started"
                description="Intro screen button text"
              />
            </Button>
            <Disclaimers
              hasInstallments={hasInstallments}
              hasSplitPay={hasSplitpay}
              hasHybridLineOfCredit={hasHybridLineOfCredit}
              installmentsPaymentTypeValues={
                exampleInstallmentsPaymentTypeValues
              }
              splitPayPaymentTypeValues={synthesizedSplitPayPaymentValues}
              hybridLineOfCreditPaymentTypeValues={
                exampleHybridLineOfCreditPaymentTypeValues
              }
            />
          </>
        );
      }}
    </IntroScreen>
  );
};

export default Intro;

const InstallmentsCard: FunctionComponent<{
  paymentTypeValues: 0 | ComputedPaymentTypeValues | null;
  installmentIcon: SVGIcon;
}> = ({ installmentIcon, paymentTypeValues }) => {
  const { trackPageView } = useTrackPageView({ mode: 'manual' });
  const intl = useIntl();
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);
  const { isVirtualCard } = useVirtualCard();
  const { aprFormatter, formatCurrency } = useFormattedNumber();
  const { disclosureSymbolInstallments } = useMerchantConfig();

  if (!paymentTypeValues) {
    return null;
  }

  return (
    <>
      <IntroCard
        icon={installmentIcon}
        className="mt-11"
        dataTestId="installments-card"
      >
        <h3 className="flex items-center justify-center text-lg font-extrabold leading-normal mt-5 mb-2 uppercase">
          <BreadLogo
            className="w-16 h-6 pl-1"
            fill="#0F2233"
            fillLogo="#0F2233"
          />
          <FormattedMessage
            defaultMessage="Installments"
            description="Intro screen installments card heading"
          />
        </h3>
        <div
          id="footnoteDisclosureSymbolInstallments-1-rf"
          className="text-base mb-2"
        >
          <span>
            <FormattedMessage
              defaultMessage="Flexible monthly payment plans starting at {minInterestRateBPS} APR"
              description="Intro screen installments card sub heading"
              values={{
                ...formattingValues,
                minInterestRateBPS: aprFormatter(
                  paymentTypeValues.minInterestRateBPS
                ),
                disclosureSymbolInstallments,
              }}
            />
            <a href="#footnoteDisclosureSymbolInstallments">
              <sup>{disclosureSymbolInstallments}</sup>
            </a>
          </span>
        </div>
        <div className="text-center">
          <Button
            theme="text"
            testID="installments-learn-more"
            onClick={() => {
              setIsLearnMoreOpen((state) => !state);
              trackPageView(PageNames.LearnMoreInstallmentOverlay);
            }}
          >
            <FormattedMessage
              defaultMessage="Learn More"
              description="Intro screen installments learn more link"
            />
          </Button>
        </div>
      </IntroCard>

      <LearnMore
        isOpen={isLearnMoreOpen}
        onClose={() => setIsLearnMoreOpen(false)}
        title={intl.formatMessage({
          defaultMessage: 'Installments',
          description: 'Intro screen installments learn more heading',
        })}
        ariaLabel={intl.formatMessage({
          defaultMessage: 'Learn More about Installments',
          description: 'Installments aria label',
        })}
        details={
          <ul className="list-disc ml-2 mb-6 list-inside">
            <li className="mb-2">
              <FormattedMessage
                defaultMessage="{lengthValues} {lengthSize,
                  plural,
                  =0 {No}
                  one {month}
                  other {month}
                }
                payment {lengthSize,
                  plural,
                  =0 {plans}
                  one {plan}
                  other {plans}
                } available<sup>{disclosureSymbolInstallments}</sup>"
                description="Installments learn more details plans available"
                values={{
                  ...formattingValues,
                  lengthValues: (
                    <FormattedList
                      value={Array.from(
                        new Set(paymentTypeValues.lengthOptions)
                      )}
                      type={'conjunction'}
                    />
                  ),
                  lengthSize: paymentTypeValues.lengthOptions.length,
                  disclosureSymbolInstallments,
                }}
              />
            </li>
            <li className="mb-2">
              <FormattedMessage
                defaultMessage="Starting at {minInterestRateBPS} APR<sup>{disclosureSymbolInstallments}</sup>"
                description="Installments learn more details starting apr"
                values={{
                  ...formattingValues,
                  minCartSize: formatCurrency(
                    toMoneyValue(paymentTypeValues.minCartSize)
                  ),
                  minInterestRateBPS: aprFormatter(
                    paymentTypeValues.minInterestRateBPS
                  ),
                  disclosureSymbolInstallments,
                }}
              />
            </li>
            <li className="mb-2">
              <FormattedMessage
                defaultMessage="No prepayment penalties"
                description="Installments learn more details prepayment penalties"
              />
            </li>
          </ul>
        }
        checkoutInstructions={
          <Accordion>
            <Section
              title={intl.formatMessage({
                defaultMessage: 'How to checkout in-store',
                description: 'Intro screen learn more heading',
              })}
            >
              <VerticalProgressBar
                steps={[
                  {
                    Title: () => (
                      <FormattedMessage
                        defaultMessage="Check eligibility"
                        description="Intro screen installments eligibility heading"
                      />
                    ),
                    Description: () => (
                      <FormattedMessage
                        defaultMessage="Checking your eligibility is quick, easy, and won’t affect your credit score."
                        description="Intro screen installments check eligibility description"
                      />
                    ),
                  },
                  {
                    Title: () => (
                      <FormattedMessage
                        defaultMessage="Select an installment plan"
                        description="Intro screen installments installment plan heading"
                      />
                    ),
                    Description: () => (
                      <FormattedMessage
                        defaultMessage="Enter your estimated purchase amount to view estimate payments. Your actual monthly payment and total interest will be based on your final purchase amount at the register."
                        description="Intro screen installments installment plan description"
                      />
                    ),
                  },
                  ...(isVirtualCard
                    ? [
                        {
                          Title: () => (
                            <FormattedMessage
                              defaultMessage="Add one-time use card to digital wallet"
                              description="Intro screen installments one-time use card title"
                            />
                          ),
                          Description: () => (
                            <>
                              <FormattedMessage
                                defaultMessage="After you select your plan, you will recieve a one-time use Bread card to add to your phone’s digital wallet."
                                description="Intro screen installments one-time use card description"
                              />
                              <Tooltip
                                id="installments-virtual-card-tooltip"
                                tip={
                                  <TooltipParagraph>
                                    <FormattedMessage
                                      defaultMessage="This card will expire 7 days after creation and is limited to one transaction per merchant."
                                      description="Intro screen installments virtual card expiration tooltip"
                                    />
                                  </TooltipParagraph>
                                }
                                triggerClassName="inline-block px-1 align-text-top"
                                dataTestID="virtual-card-tooltip"
                              >
                                <Info
                                  aria-hidden="true"
                                  className="w-4 h-4 text-theme-primary"
                                />
                              </Tooltip>
                            </>
                          ),
                        },
                      ]
                    : []),
                  {
                    Title: () => (
                      <FormattedMessage
                        defaultMessage="Check out in-store"
                        description="Intro screen installments check out in-store heading"
                      />
                    ),
                    Description: () =>
                      isVirtualCard ? (
                        <FormattedMessage
                          defaultMessage="Tap your one-time use card from your digital wallet at the register to check out. Your plan won’t take effect unless you complete your purchase at the register."
                          description="Intro screen installments check out virtual card in-store description"
                        />
                      ) : (
                        <>
                          <FormattedMessage
                            defaultMessage="Present your valid photo ID and confirmation code to a store associate at the register to check out. Your plan won’t take effect unless you complete your purchase at the register."
                            description="Intro screen installments check out in-store description"
                          />
                          <Tooltip
                            id="installments-checkout-in-store-tooltip"
                            tip={
                              <TooltipParagraph>
                                <FormattedMessage
                                  defaultMessage="Your confirmation code is limited to one transaction per merchant at their physical location."
                                  description="Intro screen installments enjoy check out in-store tooltip"
                                />
                              </TooltipParagraph>
                            }
                            triggerClassName="inline-block px-1 align-text-top"
                            dataTestID="installment-plan-tooltip"
                          >
                            <Info
                              aria-hidden="true"
                              className="w-4 h-4 text-theme-primary"
                            />
                          </Tooltip>
                        </>
                      ),
                  },
                  {
                    Title: () => (
                      <FormattedMessage
                        defaultMessage="Enjoy your purchase!"
                        description="Intro screen installments enjoy your purchase heading"
                      />
                    ),
                    Description: () => (
                      <>
                        <FormattedMessage
                          defaultMessage="Your first payment won’t be due until at least 30 days from checkout."
                          description="Intro screen installments enjoy your purchase description"
                        />
                        <Tooltip
                          id="installments-enjoy-your-purchase-tooltip"
                          tip={
                            <TooltipParagraph>
                              <FormattedMessage
                                defaultMessage="We’ll send you information about how and when to make payments via email and text. Your payment schedule and payment due dates will be located in the Bread Financial Member Portal."
                                description="Intro screen installments enjoy your purchase tooltip"
                              />
                            </TooltipParagraph>
                          }
                          triggerClassName="inline-block px-1 align-text-top"
                          dataTestID="installment-plan-tooltip"
                        >
                          <Info
                            aria-hidden="true"
                            className="w-4 h-4 text-theme-primary"
                          />
                        </Tooltip>
                      </>
                    ),
                  },
                ]}
              />
            </Section>
          </Accordion>
        }
        disclosures={
          <InstallmentsDisclosure
            paymentTypeValues={paymentTypeValues}
            disclosureSymbolInstallments={disclosureSymbolInstallments}
          />
        }
      />
    </>
  );
};

const HybridLineOfCreditCard: FunctionComponent<{
  paymentTypeValues: 0 | ComputedPaymentTypeValues | null;
  installmentIcon: SVGIcon;
}> = ({ installmentIcon, paymentTypeValues }) => {
  const { aprFormatter } = useFormattedNumber();
  const { disclosureSymbolHybridLineOfCredit } = useMerchantConfig();

  if (!paymentTypeValues) {
    return null;
  }

  return (
    <>
      <IntroCard
        icon={installmentIcon}
        className="mt-11"
        dataTestId="hyloc-card"
      >
        <h3 className="flex items-center justify-center text-lg font-extrabold leading-normal mt-5 mb-1">
          <FormattedMessage
            defaultMessage="Project Loan"
            description="Intro screen HYLOC card heading"
          />
        </h3>
        <p className="flex items-center justify-center leading-normal mb-4">
          <FormattedMessage
            defaultMessage="powered by "
            description="Intro screen HYLOC powered by sub heading"
          />

          <BreadLogo
            className="w-16 h-6 pl-1"
            fill="#0F2233"
            fillLogo="#0F2233"
          />
        </p>
        <div className="mb-4">
          <p className="font-bold">
            <FormattedMessage
              defaultMessage="One payment plan, multiple purchases"
              description="Intro screen HYLOC card heading"
            />
          </p>

          <p>
            <FormattedMessage
              defaultMessage="Shop your way for up to 6 months."
              description="Intro screen HYLOC card heading"
            />
          </p>
        </div>

        <div
          id="footnoteDisclosureSymbolHyloc-1-rf"
          className="text-base font-bold mb-2"
        >
          <span>
            <FormattedMessage
              defaultMessage="Rates as low as {minInterestRateBPS} APR<sup>{disclosureSymbolHybridLineOfCredit}</sup>"
              description="Intro screen HYLOC card sub heading"
              values={{
                ...formattingValues,
                minInterestRateBPS: aprFormatter(
                  paymentTypeValues.minInterestRateBPS
                ),
                disclosureSymbolHybridLineOfCredit,
              }}
            />
            <a href="#footnoteDisclosureSymbolHyloc">
              <sup>{disclosureSymbolHybridLineOfCredit}</sup>
            </a>
          </span>
        </div>
      </IntroCard>
    </>
  );
};

const SplitPayCard: FunctionComponent<{
  paymentTypeValues: 0 | ComputedPaymentTypeValues | null;
  splitPayIcon: SVGIcon;
}> = ({ paymentTypeValues, splitPayIcon }) => {
  const { trackPageView } = useTrackPageView({ mode: 'manual' });
  const intl = useIntl();
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);
  const { isVirtualCard } = useVirtualCard();
  const { disclosureSymbolSplitpay, disclosureSymbolSplitpayInterest } =
    useMerchantConfig();

  if (!paymentTypeValues) {
    return null;
  }

  return (
    <>
      <IntroCard
        icon={splitPayIcon}
        dataTestId="splitpay-card"
        className="mt-11"
      >
        <h3 className="flex items-center justify-center text-lg font-extrabold leading-normal mt-5 mb-2 uppercase">
          <BreadLogo
            className="w-16 h-6 pl-1"
            fill="#0F2233"
            fillLogo="#0F2233"
          />
          <FormattedMessage
            defaultMessage="SplitPay"
            description="Intro screen split-pay card heading"
          />
        </h3>

        <ul>
          <li
            id="footnoteDisclosureSymbolSplitPay-1-rf"
            className="text-base mb-2"
          >
            <span>
              <FormattedMessage
                defaultMessage="4 interest-free payments applied to your debit or credit card every 2 weeks"
                description="Intro screen split-pay card sub heading"
                values={{
                  ...formattingValues,
                  disclosureSymbolSplitpay,
                }}
              />
              <a href="#footnoteDisclosureSymbolSplitPay">
                <sup>{disclosureSymbolSplitpay}</sup>
              </a>
            </span>
          </li>
          <li
            id="footnoteDisclosureSymbolSplitPayInterest-rf"
            className="mt-4 text-base font-normal"
          >
            <span>
              <FormattedMessage
                defaultMessage="Always interest-free (0.00% APR)"
                description="Intro screen splitPay card bullet two"
                values={{
                  ...formattingValues,
                  disclosureSymbolSplitpayInterest,
                }}
              />
              <a href="#footnoteDisclosureSymbolSplitPay">
                <sup>{disclosureSymbolSplitpay}</sup>
              </a>
            </span>
          </li>
        </ul>

        <div></div>

        <div className="text-center">
          <Button
            theme="text"
            testID="split-pay-learn-more"
            onClick={() => {
              setIsLearnMoreOpen((state) => !state);
              trackPageView(PageNames.LearnMoreSplitPayOverlay);
            }}
          >
            <FormattedMessage
              defaultMessage="Learn More"
              description="Intro screen split-pay learn more link"
            />
          </Button>
        </div>
      </IntroCard>

      <LearnMore
        isOpen={isLearnMoreOpen}
        onClose={() => setIsLearnMoreOpen(false)}
        title={intl.formatMessage({
          defaultMessage: 'SplitPay',
          description: 'Intro screen split-pay learn more heading',
        })}
        ariaLabel={intl.formatMessage({
          defaultMessage: 'Learn More about SplitPay',
          description: 'SplitPay aria label',
        })}
        details={
          <>
            <ul className="list-disc ml-6 mb-4 list-outside">
              <li className="mb-2">
                <FormattedMessage
                  defaultMessage="Split your purchase into 4 interest-free payments"
                  description="Split-Pay learn more details interest-free payments"
                />
              </li>
              <li className="mb-2">
                <FormattedMessage
                  defaultMessage="Your first payment will be applied to your debit or credit card when checking out with an associate at the register"
                  description="Split-Pay learn more details payment"
                />
              </li>
              <li className="mb-2">
                <FormattedMessage
                  defaultMessage="Remaining 3 payments automatically applied to your card every 2 weeks"
                  description="Split-Pay learn more details payment frequency"
                />
              </li>
            </ul>
          </>
        }
        checkoutInstructions={
          <Accordion>
            <Section
              title={intl.formatMessage({
                defaultMessage: 'How to checkout in-store',
                description: 'Intro screen learn more heading',
              })}
            >
              <VerticalProgressBar
                steps={[
                  {
                    Title: () => (
                      <FormattedMessage
                        defaultMessage="Check eligibility"
                        description="Intro screen split-pay how to check eligibility heading"
                      />
                    ),
                    Description: () => (
                      <FormattedMessage
                        defaultMessage="Checking your eligibility is quick, easy, and won’t affect your credit score."
                        description="Intro screen split-pay how to check eligibility description"
                      />
                    ),
                  },
                  {
                    Title: () => (
                      <FormattedMessage
                        defaultMessage="Add your payment details"
                        description="Intro screen split-pay payment details heading"
                      />
                    ),
                    Description: () => (
                      <FormattedMessage
                        defaultMessage="Add a debit or credit card for automatic payments. Your card must have enough to cover 25% of the purchase at checkout. Your biweekly payments will be based on your final purchase amount at the register."
                        description="Intro screen split-pay payment details description"
                      />
                    ),
                  },
                  ...(isVirtualCard
                    ? [
                        {
                          Title: () => (
                            <FormattedMessage
                              defaultMessage="Add one-time use card to digital wallet"
                              description="Intro screen split-pay one-time use card title"
                            />
                          ),
                          Description: () => (
                            <>
                              <FormattedMessage
                                defaultMessage="After you select your plan, you will recieve a one-time use Bread card to add to your phone’s digital wallet."
                                description="Intro screen installments one-time use card description"
                              />
                              <Tooltip
                                id="splitpay-virtual-card-tooltip"
                                tip={
                                  <TooltipParagraph>
                                    <FormattedMessage
                                      defaultMessage="This card will expire 7 days after creation and is limited to one transaction per merchant."
                                      description="Intro screen installments virtual card expiration tooltip"
                                    />
                                  </TooltipParagraph>
                                }
                                triggerClassName="inline-block px-1 align-text-top"
                                dataTestID="virtual-card-tooltip"
                              >
                                <Info
                                  aria-hidden="true"
                                  className="w-4 h-4 text-theme-primary"
                                />
                              </Tooltip>
                            </>
                          ),
                        },
                      ]
                    : []),
                  {
                    Title: () => (
                      <FormattedMessage
                        defaultMessage="Check out in-store"
                        description="Intro screen split-pay eligibility heading"
                      />
                    ),
                    Description: () =>
                      isVirtualCard ? (
                        <FormattedMessage
                          defaultMessage="Tap your one-time use card from your digital wallet at the register to check out. Your plan won’t take effect unless you complete your purchase at the register."
                          description="Intro screen split-pay check out virtual card in-store description"
                        />
                      ) : (
                        <>
                          <FormattedMessage
                            defaultMessage="Present your valid photo ID and confirmation code to a store associate at the register to check out."
                            description="Intro screen split-pay eligibility description"
                          />
                          <Tooltip
                            id="splitpay-checkout-in-store-tooltip"
                            tip={
                              <TooltipParagraph>
                                <FormattedMessage
                                  defaultMessage="Your confirmation code is limited to one transaction per merchant at their physical location."
                                  description="Intro screen split-pay enjoy check out in-store tooltip"
                                />
                              </TooltipParagraph>
                            }
                            triggerClassName="inline-block px-1 align-text-top"
                            dataTestID="installment-plan-tooltip"
                          >
                            <Info
                              aria-hidden="true"
                              className="w-4 h-4 text-theme-primary"
                            />
                          </Tooltip>
                        </>
                      ),
                  },
                  {
                    Title: () => (
                      <FormattedMessage
                        defaultMessage="Enjoy your purchase!"
                        description="Intro screen split-pay how to enjoy your purchase heading"
                      />
                    ),
                    Description: () => (
                      <FormattedMessage
                        defaultMessage="Your first payment will be applied at checkout. The remaining 3 payments will be automatically applied to your card every 2 weeks."
                        description="Intro screen split-pay how to enjoy your purchase description"
                      />
                    ),
                  },
                ]}
              />
            </Section>
          </Accordion>
        }
        disclosures={
          <SplitPayDisclosure
            paymentTypeValues={paymentTypeValues}
            disclosureSymbolSplitpay={disclosureSymbolSplitpay}
          />
        }
      />
    </>
  );
};

const InstallmentsDisclosure: FunctionComponent<{
  paymentTypeValues: ComputedPaymentTypeValues | null;
  disclosureSymbolInstallments?: string;
}> = ({ paymentTypeValues, disclosureSymbolInstallments }) => {
  const { aprFormatter, formatCurrency } = useFormattedNumber();

  if (!paymentTypeValues) {
    return null;
  }

  return (
    <div data-testid="installments-disclaimer">
      <Disclaimer>
        <FormattedMessage
          defaultMessage="<sup>{disclosureSymbolInstallments}</sup>Available for purchases between
      {minCartSize} and {maxCartSize}.
      Subject to approval of credit application. Rates range from {minInterestRateBPS} to {maxInterestRateBPS} APR,
      resulting in, for example, 24 monthly payments of $50.00 at 9.99% APR, per $1,000 borrowed. APRs will vary depending
      on credit qualifications, loan amount, and term."
          description="Installments intro screen disclaimer"
          values={{
            ...formattingValues,
            minCartSize: formatCurrency(
              toMoneyValue(paymentTypeValues.minCartSize)
            ),
            maxCartSize: formatCurrency(
              toMoneyValue(paymentTypeValues.maxCartSize || 0)
            ),
            minInterestRateBPS: aprFormatter(
              paymentTypeValues.minInterestRateBPS
            ),
            maxInterestRateBPS: aprFormatter(
              paymentTypeValues.maxInterestRateBPS
            ),
            disclosureSymbolInstallments,
          }}
        />
      </Disclaimer>
    </div>
  );
};

const SplitPayDisclosure: FunctionComponent<{
  paymentTypeValues: ComputedPaymentTypeValues | null;
  disclosureSymbolSplitpay?: string;
}> = ({ paymentTypeValues, disclosureSymbolSplitpay }) => {
  const { formatCurrency } = useFormattedNumber();

  if (!paymentTypeValues) {
    return null;
  }

  return (
    <div data-testid="split-pay-disclaimer">
      <Disclaimer>
        <FormattedMessage
          defaultMessage="<sup>{disclosureSymbolSplitpay}</sup>Available for purchases between {minCartSize} and {maxCartSize}.
      Subject to approval of credit application."
          description="SplitPay intro screen disclaimer"
          values={{
            ...formattingValues,
            minCartSize: formatCurrency(
              toMoneyValue(paymentTypeValues.minCartSize)
            ),
            maxCartSize: formatCurrency(
              toMoneyValue(paymentTypeValues.maxCartSize || 0)
            ),
            disclosureSymbolSplitpay,
          }}
        />
      </Disclaimer>
    </div>
  );
};

const LearnMore: FunctionComponent<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  details: JSX.Element;
  checkoutInstructions: JSX.Element;
  disclosures: JSX.Element | null;
  ariaLabel?: string;
}> = ({
  isOpen,
  onClose,
  title,
  details,
  checkoutInstructions,
  disclosures,
  ariaLabel,
}) => {
  return (
    <SlideOut
      id="learn-more-slideout"
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={ariaLabel}
    >
      <h2 className="text-3xl font-extrabold mb-4">{title}</h2>

      {details}
      {checkoutInstructions}

      <hr className="ml-1 mr-1 mb-8 mt-2 border-light-blue-grey" />

      {disclosures}
    </SlideOut>
  );
};
